import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './client';
import type {
  AdminMetricsResponse,
  AdminUserListResponse,
  AdminUserResponse,
  AdminHealthResponse,
  UserFilter,
  AuditLogListResponse,
  AuditLogFilter,
} from './types';

// ============================================================================
// Query Keys
// ============================================================================

export const queryKeys = {
  metrics: ['admin', 'metrics'] as const,
  users: (filter?: UserFilter) => ['admin', 'users', filter] as const,
  user: (id: string) => ['admin', 'users', id] as const,
  health: ['admin', 'health'] as const,
  audit: (filter?: AuditLogFilter) => ['admin', 'audit', filter] as const,
};

// ============================================================================
// Metrics Hooks
// ============================================================================

export function useAdminMetrics(
  options?: Omit<UseQueryOptions<AdminMetricsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.metrics,
    queryFn: () => api.get<AdminMetricsResponse>('/metrics'),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
    ...options,
  });
}

// ============================================================================
// User Hooks
// ============================================================================

export function useAdminUsers(
  filter?: UserFilter,
  options?: Omit<UseQueryOptions<AdminUserListResponse>, 'queryKey' | 'queryFn'>
) {
  // Build query string from filter
  const params = new URLSearchParams();
  if (filter?.search) params.set('search', filter.search);
  if (filter?.has_business !== undefined) params.set('has_business', String(filter.has_business));
  if (filter?.sort) params.set('sort', filter.sort);
  if (filter?.limit) params.set('limit', String(filter.limit));
  if (filter?.offset) params.set('offset', String(filter.offset));

  const queryString = params.toString();
  const endpoint = queryString ? `/users?${queryString}` : '/users';

  return useQuery({
    queryKey: queryKeys.users(filter),
    queryFn: () => api.get<AdminUserListResponse>(endpoint),
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

export function useAdminUser(
  id: string,
  options?: Omit<UseQueryOptions<AdminUserResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.user(id),
    queryFn: () => api.get<AdminUserResponse>(`/users/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: 'active' | 'suspended' }) => {
      return api.patch<AdminUserResponse>(`/users/${userId}/status`, { status });
    },
    onSuccess: (data) => {
      // Invalidate and refetch user queries
      queryClient.invalidateQueries({ queryKey: queryKeys.user(data.id) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit() });
    },
  });
}

// ============================================================================
// Health Hooks
// ============================================================================

export function useAdminHealth(
  options?: Omit<UseQueryOptions<AdminHealthResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.get<AdminHealthResponse>('/health'),
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 30 * 1000, // 30 seconds
    ...options,
  });
}

// ============================================================================
// Audit Log Hooks
// ============================================================================

export function useAdminAuditLogs(
  filter?: AuditLogFilter,
  options?: Omit<UseQueryOptions<AuditLogListResponse>, 'queryKey' | 'queryFn'>
) {
  // Build query string from filter
  const params = new URLSearchParams();
  if (filter?.search) params.set('search', filter.search);
  if (filter?.user_id) params.set('user_id', filter.user_id);
  if (filter?.target_type) params.set('target_type', filter.target_type);
  if (filter?.target_id) params.set('target_id', filter.target_id);
  if (filter?.date_from) params.set('date_from', filter.date_from);
  if (filter?.date_to) params.set('date_to', filter.date_to);
  if (filter?.limit) params.set('limit', String(filter.limit));
  if (filter?.offset) params.set('offset', String(filter.offset));
  // Handle action filter (can be multiple values)
  if (filter?.action?.length) {
    filter.action.forEach((a) => params.append('action', a));
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/audit?${queryString}` : '/audit';

  return useQuery({
    queryKey: queryKeys.audit(filter),
    queryFn: () => api.get<AuditLogListResponse>(endpoint),
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format cents to dollar string
 */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Format large numbers with abbreviations
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toString();
}

/**
 * Format date to relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString();
}
